<script setup lang="ts">
import { LucideMessageSquare, LucideLoader2 } from 'lucide-vue-next'
import type { ICommentWithAuthor, ICommentFormData, ICommentsWidgetProps } from './types'
import { COMMENTS_MAX_DEPTH } from './types'
import WidgetCommentItem from './WidgetCommentItem.vue'
import WidgetCommentForm from './WidgetCommentForm.vue'
import { useUser } from '~/composables/useUser'
import { useAuthModalStore } from '~/stores/authModal'
import type { IResponseData } from '~/types'
import { useToast } from '~/components/ui/toast'
import { ANCHOR_IDS } from '~/lib/constants'
import { ContentBlock } from '~/components/ui/content-block'

const props = withDefaults(defineProps<ICommentsWidgetProps>(), {
  sortOrder: 'newest'
})

const { isLoggedIn, getToken } = useUser()
const authModalStore = useAuthModalStore()
const { toast } = useToast()
const { t } = useI18n()

const activeReplyCommentId = ref<number | null>(null)
const isSubmitting = ref(false)

// Watch for user changes and refresh comments
watch(
  () => isLoggedIn.value,
  (newIsLoggedIn, oldIsLoggedIn) => {
    // Only refresh if the login state actually changed
    if (newIsLoggedIn !== oldIsLoggedIn) {
      refreshComments()
    }
  }
)

// Fetch comments from API
const {
  data: fetchedComments,
  pending: isLoading,
  refresh: refreshComments
} = useApi<IResponseData<ICommentWithAuthor[]>>(
  () =>
    useUrlWithParams('/comments', {
      'filter[commentable_id]': props.commentableId.toString(),
      'filter[commentable_type]': props.commentableType
    }),
  {
    key: `comments-${props.commentableType}-${props.commentableId}`
  }
)

// Function to build comment tree from flat list
const buildCommentTree = (comments: ICommentWithAuthor[]): ICommentWithAuthor[] => {
  if (!comments || comments.length === 0) return []

  // Create a map for quick lookup
  const commentMap = new Map<number, ICommentWithAuthor>()

  // Initialize all comments with empty replies array
  comments.forEach((comment) => {
    commentMap.set(comment.id, { ...comment, replies: [] })
  })

  const rootComments: ICommentWithAuthor[] = []

  // Build the tree, skipping self-referential or unknown parents to prevent cycles
  comments.forEach((comment) => {
    const commentWithReplies = commentMap.get(comment.id)!

    if (comment.parent_id === null || comment.parent_id === undefined) {
      rootComments.push(commentWithReplies)
    } else if (comment.parent_id !== comment.id) {
      // Child comment - add to parent's replies
      const parent = commentMap.get(comment.parent_id)
      if (parent) {
        if (!parent.replies) {
          parent.replies = []
        }
        parent.replies.push(commentWithReplies)
      } else {
        // Parent not found — treat as root
        rootComments.push(commentWithReplies)
      }
    }
  })

  return rootComments
}

// Comments data from API - transformed to tree structure
const commentsData = computed(() => {
  const flatComments = fetchedComments.value?.data || []
  return buildCommentTree(flatComments)
})

// Recursively sort comments and their replies
const sortCommentsRecursively = (
  comments: ICommentWithAuthor[],
  order: 'newest' | 'oldest',
  depth = 0
): ICommentWithAuthor[] => {
  if (depth > COMMENTS_MAX_DEPTH) return comments

  const sorted = [...comments].sort((a, b) => {
    const dateA = new Date(a.updated_at || '').getTime()
    const dateB = new Date(b.updated_at || '').getTime()
    return order === 'newest' ? dateB - dateA : dateA - dateB
  })

  // Recursively sort replies
  return sorted.map((comment) => ({
    ...comment,
    replies: comment.replies ? sortCommentsRecursively(comment.replies, order, depth + 1) : []
  }))
}

// Sort comments based on sortOrder prop
const sortedComments = computed(() => {
  if (!commentsData.value) return []
  return sortCommentsRecursively(commentsData.value, props.sortOrder)
})

const commentsCount = computed(() => {
  const countReplies = (comments: ICommentWithAuthor[], depth = 0): number => {
    if (depth > COMMENTS_MAX_DEPTH) return comments.length
    return comments.reduce((total, comment) => {
      return total + 1 + (comment.replies ? countReplies(comment.replies, depth + 1) : 0)
    }, 0)
  }
  return countReplies(commentsData.value)
})

const openLoginModal = () => {
  authModalStore.openLoginModal()
}

const setActiveReply = (commentId: number | null) => {
  activeReplyCommentId.value = commentId
}

const handleSubmit = async (data: ICommentFormData): Promise<boolean> => {
  // Check if user is logged in
  const token = getToken()
  if (!token) {
    openLoginModal()
    return false
  }

  // Prevent double submission
  if (isSubmitting.value) return false

  try {
    isSubmitting.value = true

    // Prepare request body
    const body = {
      commentable_type: props.commentableType,
      commentable_id: props.commentableId,
      parent_id: data.parent_id || null,
      original_text: data.text
    }

    // Send POST request with authorization
    const { error } = await useApiPost<IResponseData<ICommentWithAuthor>>('/comments', body, {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    })

    if (error) {
      toast({
        title: t('error.title'),
        description:
          error && error.status === 429
            ? t('widget.comment.form.too_many_attempts')
            : t('widget.comment.form.submit_error'),
        variant: 'destructive'
      })
      return false
    }

    // Success - refresh comments list
    await refreshComments()

    // Close reply form
    activeReplyCommentId.value = null

    // Show success message
    toast({
      title: t('widget.comment.form.submit_success'),
      description: t('widget.comment.form.submit_success_description'),
      variant: 'default'
    })

    return true
  } catch (err) {
    console.error('Error submitting comment:', err)
    toast({
      title: t('error.title'),
      description: t('widget.comment.form.general_error'),
      variant: 'destructive'
    })
    return false
  } finally {
    isSubmitting.value = false
  }
}

const commentHeaderText = computed(() => {
  const header = t('widget.comment.header')
  if (commentsCount.value > 0) {
    return `${header} (${commentsCount.value})`
  }
  return header
})
</script>

<template>
  <ContentBlock
    :id="ANCHOR_IDS.WIDGET_COMMENTS"
    variant="white"
    :heading-text="commentHeaderText"
    heading-class="h4"
    heading-tag="h2"
    :show-link-icon="true"
    class="FormScrollOffset"
  >
    <!-- Loading State -->
    <template v-if="isLoading">
      <div class="flex items-center justify-center py-12">
        <LucideLoader2 class="size-8 animate-spin text-gray-400" />
      </div>
    </template>

    <!-- Comments List -->
    <template v-else-if="sortedComments.length > 0">
      <div class="divide-y divide-gray-100">
        <WidgetCommentItem
          v-for="comment in sortedComments"
          :key="comment.id"
          :comment="comment"
          :depth="0"
          :active-reply-id="activeReplyCommentId"
          :on-submit="handleSubmit"
          :on-set-active-reply="setActiveReply"
        />
      </div>
    </template>

    <!-- Empty State -->
    <template v-else>
      <div class="py-12 text-center">
        <LucideMessageSquare class="mx-auto mb-3 size-12 text-gray-300" />
        <p class="text-gray-600">{{ $t('widget.comment.empty_state.title') }}</p>
        <p class="text-sm text-gray-500">{{ $t('widget.comment.empty_state.subtitle') }}</p>
      </div>
    </template>

    <!-- Main Comment Form / Login Button -->
    <div id="comment-form">
      <template v-if="isLoggedIn">
        <WidgetCommentForm :on-submit="handleSubmit" />
      </template>
      <template v-else>
        <div class="border-t border-gray-200 py-8 text-center">
          <p class="mb-4 text-gray-600">
            {{ $t('widget.comment.login_prompt.text') }}
          </p>
          <Button variant="blue" class="px-6 py-2" @click="openLoginModal">
            {{ $t('common.sign_in') }}
          </Button>
        </div>
      </template>
    </div>
  </ContentBlock>
</template>
